import React from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import {
	Button,
	FlexColumn,
	FlexRow,
	Heading,
	Icon,
	OutlinedPillVariant,
	Pill,
	Text,
	TextField,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {TestHook} from '../TestHook';

const tokens = tokensForThemeProvider.sky.sys;

const HeaderWrapper = styled(FlexColumn)`
	gap: ${tokens.spacing['100']};
`;

const NameRow = styled(FlexRow)`
	gap: ${tokens.spacing['200']};
`;

/* A line of provenance, not a message: small auxiliary-grey detail text sitting on the heading's
 * baseline row rather than a full-width alert. `nowrap` keeps the date on one line so it never
 * wraps under the report name. */
/** The design writes the publish date out in full ("17 August 2026") rather than as slashes, so the
 * caption reads as a sentence next to the report name instead of a data field. Only the parts are
 * pinned here, never the order: `formatDate` still lays them out per locale, so this renders
 * "17 August 2026" where the locale puts the day first and "August 17, 2026" where it puts the month
 * first. Same principle the date-range control follows - the presentation is normalised, the ordering
 * stays the locale's business. */
const PUBLISHED_DATE_FORMAT = {day: 'numeric', month: 'long', year: 'numeric'} as const;

/* detail 1 (12px), not detail 2 (10px): every other secondary label in the app uses this size, and
 * 10px reads as fine print rather than provenance beside a 24px heading. */
const PublishedCaption = styled.span`
	${tokens.font.detail['1']}
	color: ${tokens.color.text.content.auxiliary};
	white-space: nowrap;
`;

/** Holds the report name and the published caption in their OWN baseline group, so the caption sits on
 * the title's baseline the way the design draws it (the caption's box bottom lines up with the
 * heading's, reading as a trailing annotation on the title rather than a separate item beside it).
 *
 * This nesting is load-bearing, not decoration. Putting align-self: baseline on the caption alone does
 * nothing useful: in flexbox a baseline-aligned item only aligns against OTHER baseline-aligned items,
 * and the surrounding row centres its children, so a lone baseline item forms a group of one and falls
 * back to behaving like flex-start - the caption ends up level with the TOP of the title. Both texts
 * have to share a container that is itself baseline-aligned.
 *
 * Kept separate from the surrounding row so the pencil and the unsaved-changes pill stay centred; they
 * are icons and a pill, and have no baseline worth sharing with a heading. Gap matches the design. */
const TitleGroup = styled.div`
	display: flex;
	align-items: baseline;
	gap: ${tokens.spacing['200']};
	min-width: 0;
`;

/* the design: breadcrumb "Reports": 16px / weight 350 / #232334. DS Text (size 1) already
 * gives 16px + the dark main colour, but its body token weight is heavier than 350; pin the weight
 * so the eyebrow matches the design's lighter breadcrumb. */
const ReportsEyebrow = styled(Text)`
	font-weight: 350;
`;

/* The breadcrumb is the way back to the reports list, matching how every other saved report is left.
 * A reset <button> rather than a DS Button so it keeps the breadcrumb's plain text appearance while
 * staying keyboard-reachable and announced as a control. */
const BreadcrumbButton = styled.button`
	display: inline-flex;
	align-items: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	text-align: left;
`;

/* the design: renders the name-edit affordance as a plain dark pencil glyph (#232334),
 * not a brand-coloured low-emphasis Button (which tints the icon blue). A reset <button> keeps it
 * keyboard-accessible while letting the DS Icon own the colour via newColor="onlight" (#232334). */
const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
`;

/** Page-header block sitting above the period/date/buttons controls row: a small "Reports"
 * eyebrow, then the report name as a large heading. Editors additionally see a trailing pencil
 * icon button that opens an inline field with a staged draft name — staged in
 * `useReportBuilder().pendingName`, so nothing is written back to `definition.name` until the
 * submit (tick) button or Enter runs `commitPendingName()`; the revert (trash) button or Escape
 * discards the draft instead by clearing `pendingName` directly. Readers (and view mode) get a
 * read-only heading only, with no pencil — plus, once the report has been published, an inline
 * caption beside the name saying when that last happened. */
export function ReportPageHeader(): JSX.Element {
	const {formatMessage, formatDate} = useIntl();
	const history = useHistory();
	const {definition, dirty, mode, role, publishedAt, pendingName, setPendingName, commitPendingName} = useReportBuilder();

	const canEditName = role === 'editor' && mode === 'edit';
	const isEditingName = pendingName !== null;
	const trimmedDraft = (pendingName ?? '').trim();
	const canSubmit = trimmedDraft !== '';

	const openEditor = (): void => {
		setPendingName(definition.name);
	};

	const commit = (): void => {
		if (!canSubmit) {
			return;
		}
		commitPendingName();
	};

	const revert = (): void => setPendingName(null);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Escape') {
			revert();
			return;
		}
		if (event.key !== 'Enter') {
			return;
		}
		// Enter on a focused button (submit or revert) triggers that button's own native activation;
		// treating it as a commit here too would double-apply it for submit and, worse, apply a
		// commit through the revert button a moment before its own click discards the draft instead.
		if ((event.target as HTMLElement).tagName === 'BUTTON') {
			return;
		}
		commit();
	};

	return (
		<TestHook data-cy={'ai-report-page-header'}>
			<HeaderWrapper>
				{/* the design: the "Reports" eyebrow/breadcrumb is the dark main content colour
				 * (#232334) at 16px, NOT a muted/auxiliary grey. `Text` already defaults to `color.main`
				 * (#232334) — the previous `auxiliary` override was the wrong (grey) token. */}
				<BreadcrumbButton
					onClick={() => history.push('/reports')}
					aria-label={formatMessage({id: 'ai_report_builder.all_reports'})}
					data-cy={'ai-report-all-reports-button'}
				>
					<FlexRow alignItems={'center'} gap={'xs'}>
						<Icon icon={'chevronLeft'} newColor={'onlight'} size={'s'} />
						<ReportsEyebrow>{formatMessage({id: 'ai_report_builder.all_reports'})}</ReportsEyebrow>
					</FlexRow>
				</BreadcrumbButton>
				{isEditingName && canEditName ? (
					// The key handling lives on a plain wrapper div, as it did before: `TextField`
					// takes no key handler of its own, and a DS layout component is not guaranteed
					// to forward one to its DOM node.
					<div onKeyDown={handleKeyDown}>
						<NameRow alignItems={'center'}>
							<TextField
								value={pendingName ?? ''}
								onValueChange={setPendingName}
								autofocus
								aria-label={formatMessage({id: 'ai_report_builder.report_name'})}
								data-cy={'ai-report-name-input'}
							/>
							<Button
								iconOnly
								emphasis={'high'}
								icon={'tick'}
								disabled={!canSubmit}
								onPress={commit}
								aria-label={formatMessage({id: 'ai_report_builder.submit_report_name'})}
								data-cy={'ai-report-name-submit-button'}
							/>
							{/* A reset button rather than a DS Button so the glyph keeps the plain
							    dark on-light colour the pencil beside it uses, instead of the brand
							    tint a low-emphasis Button applies to its icon. */}
							<IconButton
								onClick={revert}
								aria-label={formatMessage({id: 'ai_report_builder.revert_report_name'})}
								data-cy={'ai-report-name-revert-button'}
							>
								<Icon icon={'trash'} newColor={'onlight'} size={'m'} />
							</IconButton>
						</NameRow>
					</div>
				) : (
					<NameRow alignItems={'center'}>
						<TitleGroup>
							<Heading size={'3'}>{definition.name}</Heading>
							{/* When the report was last handed to readers, stated beside the name it
							    belongs to. View mode only: an editor is told where readers stand by
							    the publish-state banner instead, and a report that has never been
							    published has no date to state. Rendered as a sibling of `Heading`
							    and resolved to a plain string, because DS text components treat
							    children as text and drop nested elements. The date parts are
							    pinned; their order stays locale-driven. */}
							{mode === 'view' && publishedAt !== null ? (
								<PublishedCaption data-cy={'ai-report-last-published'}>
									{formatMessage(
										{id: 'ai_report_builder.last_published'},
										{date: formatDate(publishedAt, PUBLISHED_DATE_FORMAT)}
									)}
								</PublishedCaption>
							) : null}
						</TitleGroup>
						{canEditName ? (
							<IconButton
								onClick={openEditor}
								aria-label={formatMessage({id: 'ai_report_builder.edit_report_name'})}
								data-cy={'ai-report-name-edit-button'}
							>
								<Icon icon={'edit'} newColor={'onlight'} size={'m'} />
							</IconButton>
						) : null}
						{/* States that the report has changes not yet handed to readers. Driven by
						    `dirty` alone: a never-saved draft the user has not touched has nothing
						    they changed, and a save that landed without its publish is genuinely
						    saved — the publish-state banner speaks for that case. `canEditName`
						    already means editor + edit mode, the only combination where unsaved
						    changes are possible. */}
						{canEditName && dirty ? (
							<Pill outlined outlinedVariant={OutlinedPillVariant.ACTIVE}>
								{formatMessage({id: 'ai_report_builder.unsaved_changes'})}
							</Pill>
						) : null}
					</NameRow>
				)}
			</HeaderWrapper>
		</TestHook>
	);
}
